// Booking-types (BookingKind) admin manager — design-notes-booking-types-reasons-titles Feature A/B.
// Dedicated lighter manager (NOT the shared ResourceManager/DeactivateDialog — these soft-delete-only
// entities have no orphan-cascade model). List incl. inactive; add/edit (name, colour, behaviour);
// retire = soft-delete DELETE (shows the real in-use count, A1); reactivate = PUT is_active:true.

import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { C, F, card, Btn, StatusPill, Modal, ErrorText } from './adminUi';
import * as api from '../../lib/admin';

const rowStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderBottom: `1px solid ${C.border}` };
const labelStyle: CSSProperties = { fontWeight: 600, fontSize: 12, color: C.textDim, marginBottom: 3, display: 'block' };
const inputStyle: CSSProperties = { fontFamily: F, fontSize: 13, padding: '6px 8px', border: `1px solid ${C.borderMed}`, borderRadius: 6, background: '#fff', color: C.text, width: '100%' };

// Behaviour is a 3-way choice — structurally mutually exclusive (is_maintenance XOR is_unavailability),
// mirroring the server guard. 'normal' = both false.
type Behaviour = 'normal' | 'maintenance' | 'unavailability';
const behaviourOf = (k: api.AdminBookingKind): Behaviour => k.is_maintenance ? 'maintenance' : k.is_unavailability ? 'unavailability' : 'normal';
const flagsFor = (b: Behaviour) => ({ is_maintenance: b === 'maintenance', is_unavailability: b === 'unavailability' });
const behaviourLabel: Record<Behaviour, string> = { normal: 'Booking type', maintenance: 'Maintenance', unavailability: 'Unavailable (resource blocker)' };

interface FormState { name: string; ui_color: string; behaviour: Behaviour }
const blank: FormState = { name: '', ui_color: '#0369a1', behaviour: 'normal' };

export default function BookingKindsManager({ setToast }: { setToast: (s: string | null) => void }) {
  const [rows, setRows] = useState<api.AdminBookingKind[]>([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [modal, setModal] = useState<{ kind: 'form'; editing: api.AdminBookingKind | null } | { kind: 'retire'; row: api.AdminBookingKind } | null>(null);

  const load = useCallback(() => { api.listBookingKinds(includeInactive).then(setRows); }, [includeInactive]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => load();

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <h1 style={{ fontSize: 17, margin: 0 }}>Booking types</h1>
        <label style={{ marginLeft: 16, fontSize: 12.5, color: C.textDim, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} style={{ accentColor: C.accent }} /> include inactive
        </label>
        <div style={{ marginLeft: 'auto' }}><Btn kind="primary" onClick={() => setModal({ kind: 'form', editing: null })}>+ New type</Btn></div>
      </div>
      <p style={{ color: C.textDim, fontSize: 12.5, margin: '0 0 12px' }}>
        Colours and behaviour for booking blocks. <b>Maintenance</b> drives the U/S lifecycle; <b>Unavailable</b> blocks a
        resource (aircraft, room, or staff) without a client/student. Retiring keeps existing bookings; it just hides the
        type from new bookings.
      </p>

      {rows.length === 0 ? <div style={{ color: C.textMuted, fontStyle: 'italic', padding: 16 }}>None.</div> : rows.map((r) => {
        const beh = behaviourOf(r);
        return (
          <div key={r.id} style={rowStyle}>
            <span title={r.ui_color} style={{ width: 16, height: 16, borderRadius: 4, background: r.ui_color, border: `1px solid ${C.borderMed}`, flexShrink: 0 }} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{r.name} <StatusPill active={r.is_active} /></div>
              <div style={{ fontSize: 12, color: C.textDim, marginTop: 2 }}>{behaviourLabel[beh]} · {r.ui_color}</div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {r.is_active ? <>
                <Btn kind="ghost" onClick={() => setModal({ kind: 'form', editing: r })}>Edit</Btn>
                <Btn kind="danger" onClick={() => setModal({ kind: 'retire', row: r })}>Retire</Btn>
              </> : <Btn kind="accent" onClick={async () => { const res = await api.reactivateBookingKind(r.id); if (!res.error) { setToast(`${r.name} reactivated.`); refresh(); } else setToast(res.error); }}>Reactivate</Btn>}
            </div>
          </div>
        );
      })}

      {modal?.kind === 'form' && (
        <KindForm editing={modal.editing} onClose={() => setModal(null)}
          onSaved={(msg) => { setModal(null); setToast(msg); refresh(); }} />
      )}
      {modal?.kind === 'retire' && (
        <RetireDialog row={modal.row} onClose={() => setModal(null)}
          onDone={() => { setModal(null); setToast(`${modal.row.name} retired.`); refresh(); }} />
      )}
    </div>
  );
}

function KindForm({ editing, onClose, onSaved }: { editing: api.AdminBookingKind | null; onClose: () => void; onSaved: (msg: string) => void }) {
  const [form, setForm] = useState<FormState>(editing ? { name: editing.name, ui_color: editing.ui_color, behaviour: behaviourOf(editing) } : blank);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(editing ? { name: editing.name, ui_color: editing.ui_color, behaviour: behaviourOf(editing) } : blank);

  const submit = async () => {
    setError(null);
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!/^#[0-9A-Fa-f]{6}$/.test(form.ui_color)) { setError('Colour must be a #RRGGBB hex value.'); return; }
    setBusy(true);
    const body = { name: form.name.trim(), ui_color: form.ui_color, ...flagsFor(form.behaviour) };
    const res = editing ? await api.updateBookingKind(editing.id, body) : await api.createBookingKind(body);
    setBusy(false);
    if (res.error) { setError(res.error); return; }
    onSaved(editing ? 'Saved.' : 'Booking type created.');
  };

  return (
    <Modal title={editing ? `Edit ${editing.name}` : 'New booking type'} onClose={onClose} width={460} dirty={dirty}>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Name <span style={{ color: C.red }}>*</span></label>
        <input value={form.name} placeholder="e.g. Nav, Circuits, Staff Meeting" onChange={(e) => setForm({ ...form, name: e.target.value })} style={inputStyle} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Colour</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input type="color" value={form.ui_color} onChange={(e) => setForm({ ...form, ui_color: e.target.value })} style={{ width: 44, height: 32, border: `1px solid ${C.borderMed}`, borderRadius: 6, background: '#fff', cursor: 'pointer', padding: 2 }} />
          <input value={form.ui_color} onChange={(e) => setForm({ ...form, ui_color: e.target.value })} style={{ ...inputStyle, width: 110, fontFamily: 'monospace' }} />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Behaviour</label>
        {(['normal', 'maintenance', 'unavailability'] as Behaviour[]).map((b) => (
          <label key={b} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 13, color: C.text, cursor: 'pointer' }}>
            <input type="radio" name="behaviour" checked={form.behaviour === b} onChange={() => setForm({ ...form, behaviour: b })} style={{ accentColor: C.accent }} />
            {behaviourLabel[b]}
          </label>
        ))}
        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>A type is exactly one of these — maintenance and unavailable are mutually exclusive.</div>
      </div>
      <ErrorText>{error}</ErrorText>
      <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
        <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
        <Btn kind="primary" disabled={busy} onClick={submit}>{editing ? 'Save' : 'Create'}</Btn>
      </div>
    </Modal>
  );
}

function RetireDialog({ row, onClose, onDone }: { row: api.AdminBookingKind; onClose: () => void; onDone: () => void }) {
  const [count, setCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { api.bookingKindImpact(row.id).then(setCount); }, [row.id]);

  const confirm = async () => {
    setBusy(true); setError(null);
    const res = await api.retireBookingKind(row.id);
    setBusy(false);
    if (res.error) { setError(res.error); return; }
    onDone();
  };

  return (
    <Modal title={`Retire ${row.name}`} onClose={onClose} width={440}>
      <p style={{ fontSize: 13, color: C.text, margin: '0 0 8px' }}>
        {count === null ? 'Checking how many bookings use this type…'
          : count === 0 ? 'No bookings use this type yet.'
          : `${count} booking${count === 1 ? '' : 's'} (past and upcoming) use this type — they keep it. It just can't be selected for new bookings.`}
      </p>
      <p style={{ fontSize: 12.5, color: C.textDim, margin: 0 }}>Retiring is reversible — you can reactivate it later. Nothing is deleted or orphaned.</p>
      <ErrorText>{error}</ErrorText>
      <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
        <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
        <Btn kind="danger" disabled={busy || count === null} onClick={confirm}>Retire type</Btn>
      </div>
    </Modal>
  );
}
